/**
 * Tokens visuais — Chat interno da equipe.
 * Estrutura de layout espelha o inbox dinâmico (DynamicWhatsAppInbox).
 */
import type { Theme } from '@mui/material/styles';
import { AGENDA_THEME } from '../agendaTheme';
import {
  DOCK_TEAM_CHAT_CARDS_RIGHT,
  DOCK_TEAM_FAB_RIGHT,
  DOCK_TEAM_FAB_SIZE,
} from '../../components/layout/floatingDockLayout';
import {
  inboxChatAreaSx,
  inboxComposerPaperSx,
  inboxConversationListPaperSx,
  inboxMessageInputSx,
} from '../Inbox/inboxTheme';

export const TEAM_CHAT_LIST_WIDTH = 300;

/** Camadas do dock flutuante — popovers do composer devem ficar acima das janelas. */
export const TEAM_CHAT_DOCK_BAR_HEIGHT = 54;
/** FAB canto inferior direito + faixa de chips à esquerda dele. */
export const TEAM_CHAT_DOCK_FAB_SIZE = DOCK_TEAM_FAB_SIZE;
export const TEAM_CHAT_DOCK_FAB_RIGHT = DOCK_TEAM_FAB_RIGHT;
/** @deprecated Use DOCK_GAP_BETWEEN_FABS em floatingDockLayout */
export const TEAM_CHAT_DOCK_FAB_GAP = 4;
/** Borda direita dos chips/janelas — à esquerda do balão da Central de suporte. */
export const TEAM_CHAT_DOCK_BAR_RIGHT = DOCK_TEAM_CHAT_CARDS_RIGHT;
export const TEAM_CHAT_DOCK_WINDOW_WIDTH = 344;
export const TEAM_CHAT_DOCK_WINDOW_GAP = 12;
export const TEAM_CHAT_DOCK_WINDOW_STEP = TEAM_CHAT_DOCK_WINDOW_WIDTH + TEAM_CHAT_DOCK_WINDOW_GAP;

/**
 * Camadas do dock flutuante — acima de Dialog da agenda/prontuário (portal no body).
 * Base em snackbar evita perder para modais empilhados (1300, 1301, …).
 */
export function getTeamChatDockZ(theme: Theme) {
  const base = theme.zIndex.snackbar + 24;
  return {
    window: base,
    windowFocused: base + 1,
    launcherPanel: base + 2,
    dockBar: base + 3,
    launcherFab: base + 4,
    /** Popovers/menus do composer e diálogos auxiliares do chat. */
    overlay: base + 8,
  } as const;
}

/** @deprecated Use getTeamChatDockZ(theme) — mantido só para referência estática. */
export const TEAM_CHAT_DOCK_Z = {
  window: 1211,
  windowFocused: 1212,
  launcherPanel: 1223,
  dockBar: 1224,
  launcherFab: 1225,
  overlay: 1600,
} as const;

export const TEAM_CHAT_THEME = {
  ...AGENDA_THEME,
  bubbleMine: '#2563EB',
  bubbleMineText: '#F8FAFF',
  bubbleMineBorder: 'rgba(37, 99, 235, 0.35)',
  bubbleTheirs: '#FFFFFF',
  bubbleTheirsBorder: '#E2E8F0',
  bubbleSystemBg: 'rgba(100, 116, 139, 0.1)',
  threadBg: '#F0FAF9',
  onlineDot: '#22C55E',
  offlineDot: '#CBD5E1',
  unreadBadge: '#EF4444',
  mentionBg: 'rgba(37, 99, 235, 0.12)',
  listWidth: TEAM_CHAT_LIST_WIDTH,
} as const;

const clinicalGrid = {
  backgroundImage: `
    linear-gradient(${TEAM_CHAT_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${TEAM_CHAT_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const teamChatShellSx = {
  flex: 1,
  minHeight: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'row' as const,
  overflow: 'hidden',
  backgroundColor: TEAM_CHAT_THEME.muted,
  ...clinicalGrid,
};

export const teamChatListPaneSx = (visible: boolean) => ({
  ...inboxConversationListPaperSx('light'),
  display: visible ? 'flex' : 'none',
  flex: { xs: visible ? 1 : 0, md: `0 0 ${TEAM_CHAT_LIST_WIDTH}px` },
  minWidth: { xs: 'auto', md: TEAM_CHAT_LIST_WIDTH },
  width: { xs: '100%', md: 'auto' },
  zIndex: { xs: visible ? 2 : 1, md: 1 },
  position: { xs: 'relative' as const, md: 'static' as const },
});

export const teamChatListHeaderSx = {
  px: 2,
  pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 14px)', md: 2 },
  pb: 1.5,
  borderBottom: `1px solid ${TEAM_CHAT_THEME.borderSoft}`,
  bgcolor: TEAM_CHAT_THEME.muted,
  flexShrink: 0,
  ...clinicalGrid,
};

export const teamChatTitleSx = {
  fontWeight: 700,
  fontSize: '1.02rem',
  letterSpacing: '-0.02em',
  color: TEAM_CHAT_THEME.navy,
  lineHeight: 1.2,
};

export const teamChatSubtitleSx = {
  fontSize: '0.74rem',
  color: TEAM_CHAT_THEME.slate,
  fontWeight: 500,
};

export const teamChatSearchFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '11px',
    bgcolor: TEAM_CHAT_THEME.paper,
    fontSize: '0.85rem',
    '& fieldset': { borderColor: TEAM_CHAT_THEME.borderSoft },
    '&:hover fieldset': { borderColor: TEAM_CHAT_THEME.primary },
    '&.Mui-focused fieldset': { borderColor: TEAM_CHAT_THEME.primary },
  },
};

export const teamChatScrollAreaSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto' as const,
  overscrollBehavior: 'contain' as const,
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(15, 23, 42, 0.16)',
    borderRadius: 3,
    '&:hover': { background: 'rgba(15, 23, 42, 0.26)' },
  },
};

export const teamChatConversationItemSx = (selected: boolean) => ({
  cursor: 'pointer',
  px: 1.5,
  py: 1.35,
  mx: 1,
  my: 0.4,
  borderRadius: 2.5,
  alignItems: 'flex-start',
  gap: 1.25,
  transition: TEAM_CHAT_THEME.transition,
  bgcolor: selected ? TEAM_CHAT_THEME.primarySoft : 'transparent',
  borderLeft: selected ? `3px solid ${TEAM_CHAT_THEME.primary}` : '3px solid transparent',
  '&:hover': { bgcolor: selected ? 'rgba(37, 99, 235, 0.12)' : TEAM_CHAT_THEME.mutedAlt },
});

export const teamChatUnreadPillSx = {
  minWidth: 20,
  height: 20,
  px: 0.7,
  borderRadius: '10px',
  bgcolor: TEAM_CHAT_THEME.unreadBadge,
  color: '#fff',
  fontSize: '0.68rem',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 1,
};

export const teamChatThreadPaneSx = (visible: boolean) => ({
  flex: { xs: visible ? 1 : 0, md: 1 },
  minWidth: 0,
  minHeight: 0,
  display: visible ? 'flex' : 'none',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  width: { xs: '100%', md: 'auto' },
  zIndex: { xs: visible ? 2 : 1, md: 1 },
  position: { xs: 'relative' as const, md: 'static' as const },
});

/** Cabeçalho da conversa — Paper como no inbox. */
export const teamChatThreadHeaderPaperSx = {
  p: 2,
  borderRadius: 0,
  borderBottom: `1px solid ${TEAM_CHAT_THEME.borderSoft}`,
  bgcolor: TEAM_CHAT_THEME.paper,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  flexShrink: 0,
};

/** Área de mensagens — inboxChatAreaSx + scroll interno. */
export const teamChatChatAreaSx = inboxChatAreaSx('light');

export const teamChatMessagesScrollSx = {
  flex: 1,
  overflow: 'auto' as const,
  position: 'relative' as const,
  minHeight: 0,
  px: { xs: 1.1, sm: 1.5, md: 2 },
  pb: { xs: 1.25, md: 2 },
};

export const teamChatMessagesColumnSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 1,
};

/** Linha de mensagem — alinhamento igual ao DynamicWhatsAppInbox. */
export const teamChatMessageRowSx = (mine: boolean) => ({
  display: 'flex',
  width: '100%',
  justifyContent: mine ? 'flex-end' : 'flex-start',
  alignItems: 'flex-end',
  gap: { xs: 0.5, md: 1 },
  mb: { xs: 0.35, md: 0.5 },
  '&:hover .team-chat-actions': { opacity: 1 },
});

/** Bolha — conteúdo dentro de wrapper com maxWidth 70% na linha da mensagem. */
export const teamChatMessageBubbleSx = (mine: boolean) => ({
  p: { xs: 1.1, sm: 1.35, md: 2 },
  width: '100%',
  bgcolor: mine ? TEAM_CHAT_THEME.bubbleMine : TEAM_CHAT_THEME.bubbleTheirs,
  color: mine ? TEAM_CHAT_THEME.bubbleMineText : TEAM_CHAT_THEME.navy,
  borderRadius: mine
    ? { xs: '14px 14px 4px 14px', md: '18px 18px 4px 18px' }
    : { xs: '14px 14px 14px 4px', md: '18px 18px 18px 4px' },
  boxShadow: mine
    ? '0 2px 8px rgba(37, 99, 235, 0.22)'
    : '0 2px 4px rgba(15, 23, 42, 0.08)',
  border: '1px solid',
  borderColor: mine ? TEAM_CHAT_THEME.bubbleMineBorder : TEAM_CHAT_THEME.bubbleTheirsBorder,
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
  position: 'relative' as const,
});

/** Limita largura da bolha com base na linha (100% do chat), evitando colapso em flex-end. */
export const teamChatMessageBubbleWrapSx = {
  maxWidth: { xs: '88%', sm: '80%', md: '70%' },
  width: 'max-content',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'stretch',
};

export const teamChatBubbleBodySx = {
  fontSize: { xs: '0.78rem', sm: '0.8125rem', md: '0.875rem' },
  lineHeight: { xs: 1.45, md: 1.5 },
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
};

export const teamChatBubbleMetaSx = (mine: boolean) => ({
  display: 'flex',
  flexDirection: 'row' as const,
  flexWrap: 'nowrap' as const,
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 0.5,
  mt: 0.4,
  fontSize: { xs: '0.6rem', sm: '0.63rem', md: '0.66rem' },
  fontWeight: 500,
  whiteSpace: 'nowrap' as const,
  color: mine ? 'rgba(248, 250, 255, 0.78)' : TEAM_CHAT_THEME.slateFaint,
});

export const teamChatQuoteSx = (mine: boolean) => ({
  mb: 0.75,
  px: 1,
  py: 0.6,
  borderRadius: 1.25,
  borderLeft: `3px solid ${mine ? 'rgba(248,250,255,0.6)' : TEAM_CHAT_THEME.primary}`,
  bgcolor: mine ? 'rgba(255,255,255,0.14)' : TEAM_CHAT_THEME.primarySoft,
});

export const teamChatDayDividerSx = {
  alignSelf: 'center',
  my: 1.5,
  px: 1.5,
  py: 0.4,
  borderRadius: 999,
  bgcolor: 'rgba(15, 23, 42, 0.06)',
  color: TEAM_CHAT_THEME.slateMid,
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  textTransform: 'capitalize' as const,
};

export const teamChatSystemMessageSx = {
  alignSelf: 'center',
  maxWidth: { xs: '92%', md: '80%' },
  my: { xs: 0.5, md: 0.75 },
  px: { xs: 1.1, md: 1.5 },
  py: { xs: 0.45, md: 0.6 },
  borderRadius: 2,
  bgcolor: TEAM_CHAT_THEME.bubbleSystemBg,
  color: TEAM_CHAT_THEME.slateMid,
  fontSize: { xs: '0.68rem', sm: '0.71rem', md: '0.74rem' },
  fontWeight: 500,
  textAlign: 'center' as const,
};

export const teamChatReactionChipSx = (active: boolean) => ({
  height: 22,
  px: 0.5,
  borderRadius: 999,
  fontSize: '0.72rem',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 0.35,
  border: '1px solid',
  borderColor: active ? TEAM_CHAT_THEME.primary : TEAM_CHAT_THEME.borderSoft,
  bgcolor: active ? TEAM_CHAT_THEME.primarySoft : TEAM_CHAT_THEME.paper,
  color: active ? TEAM_CHAT_THEME.primary : TEAM_CHAT_THEME.slateMid,
  transition: TEAM_CHAT_THEME.transition,
  '&:hover': { borderColor: TEAM_CHAT_THEME.primary },
});

export const teamChatComposerPaperSx = inboxComposerPaperSx;

export const teamChatComposerFieldSx = inboxMessageInputSx('light');

export const teamChatSendButtonSx = {
  bgcolor: TEAM_CHAT_THEME.primary,
  color: '#fff',
  width: 40,
  height: 40,
  '&:hover': { bgcolor: '#1D4ED8' },
  '&.Mui-disabled': { bgcolor: TEAM_CHAT_THEME.borderSoft, color: TEAM_CHAT_THEME.slateFaint },
};

export const teamChatIconButtonSx = {
  color: TEAM_CHAT_THEME.slate,
  '&:hover': { color: TEAM_CHAT_THEME.primary, bgcolor: TEAM_CHAT_THEME.primarySoft },
};

export const teamChatEmptyStateSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  px: 4,
  textAlign: 'center' as const,
  color: TEAM_CHAT_THEME.slate,
};

/** Ponto de presença sobreposto ao avatar. */
export const teamChatPresenceDotSx = (online: boolean) => ({
  width: 11,
  height: 11,
  borderRadius: '50%',
  bgcolor: online ? TEAM_CHAT_THEME.onlineDot : TEAM_CHAT_THEME.offlineDot,
  border: '2px solid #fff',
  boxShadow: online ? `0 0 0 2px rgba(34, 197, 94, 0.2)` : 'none',
});

export const teamChatUnitChipSx = {
  height: 18,
  fontSize: '0.62rem',
  fontWeight: 600,
  bgcolor: TEAM_CHAT_THEME.tealSoft,
  color: TEAM_CHAT_THEME.tealDark,
  border: `1px solid ${TEAM_CHAT_THEME.border}`,
  '& .MuiChip-label': { px: 0.7 },
};

export const teamChatTypingSx = {
  px: { xs: 2, md: 3 },
  pb: 0.5,
  fontSize: '0.72rem',
  fontStyle: 'italic' as const,
  color: TEAM_CHAT_THEME.primary,
  fontWeight: 500,
  minHeight: 18,
};
